import { useQuery } from '@tanstack/react-query'
import { EmployeeService, TalentRankedDeveloper } from '../../services/EmployeeService'
import { ReviewService } from '../../services/ReviewService'
import history from '../../utils/history'
import { createPathToEmployeeResults } from '../../utils/employeePathUtils'
import Spinner from '../atoms/Spinner'
import ErrorMessage from '../organisms/ErrorMessage'
import { TopTalentTable } from '../organisms/TopTalentTable'
import { AppButton } from '../atoms/AppButton/AppButton'
import DownloadIcon from '../atoms/Icons/DownloadIcon'
import { useState } from 'react'
import AppFilterButton from '../atoms/AppFilterButton/AppFilterButton'
import { AVAILABLE_EMPLOYEE_HIGHLIGHTS, EMPLOYEE_HIGHLIGHT_ICON_MAP } from '../../constants/employee'
import { Review } from '../../types/definitions/review'
import { QuestionTypeEnum } from '../../types/enums/question-type.enum'

type ExportColumn =
  | 'id'
  | 'name'
  | 'email'
  | 'job_title'
  | 'role'
  | 'salary_grade'
  | 'ms_id'
  | 'manager'
  | 'category'
  | 'Employee ID'
  | 'Job Function'
  | 'communication_avg'
  | 'professional_skills_avg'
  | 'commitment_avg'
  | 'growth_mindset_avg'
  | 'productivity_avg'
  | 'ai_avg'
  | 'selected_category'
  | 'suggested_category'
  | 'override_reason'

type ExportRow = Record<ExportColumn, string>

const EXPORT_COLUMNS: ExportColumn[] = [
  'id',
  'name',
  'email',
  'job_title',
  'role',
  'salary_grade',
  'ms_id',
  'manager',
  'category',
  'Employee ID',
  'Job Function',
  'communication_avg',
  'professional_skills_avg',
  'commitment_avg',
  'growth_mindset_avg',
  'productivity_avg',
  'ai_avg',
  'selected_category',
  'suggested_category',
  'override_reason',
]

const CATEGORY_TOPIC_ALIASES: Record<
  | 'communication_avg'
  | 'professional_skills_avg'
  | 'commitment_avg'
  | 'growth_mindset_avg'
  | 'productivity_avg'
  | 'ai_avg',
  string[]
> = {
  communication_avg: ['Communication'],
  professional_skills_avg: ['Professional Skills'],
  commitment_avg: ['Commitment'],
  growth_mindset_avg: ['Growth Mindset'],
  productivity_avg: ['Productivity'],
  ai_avg: ['AI', 'Ai', 'Artificial Intelligence'],
}

const escapeCsvValue = (value: string): string => {
  const normalizedValue = value.replace(/"/g, '""')
  return `"${normalizedValue}"`
}

const buildTopTalentCsv = (rows: ExportRow[]): string => {
  return [EXPORT_COLUMNS, ...rows.map(row => EXPORT_COLUMNS.map(column => row[column]))]
    .map(row => row.map(value => escapeCsvValue(value)).join(','))
    .join('\n')
}

const parseMetadata = (metadata?: string): Record<string, unknown> => {
  if (!metadata) {
    return {}
  }

  try {
    const parsed = JSON.parse(metadata)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

const getMetadataString = (metadata: Record<string, unknown>, aliases: string[]): string => {
  const normalizedEntries = Object.entries(metadata).reduce<Record<string, unknown>>((acc, [key, value]) => {
    acc[key.toLowerCase().replace(/[^a-z0-9]/g, '')] = value
    return acc
  }, {})

  for (const alias of aliases) {
    const normalizedAlias = alias.toLowerCase().replace(/[^a-z0-9]/g, '')
    const value = normalizedEntries[normalizedAlias]
    if (value === undefined || value === null) {
      continue
    }
    return String(value)
  }

  return ''
}

const getEmployeeFieldString = (employee: unknown, field: string): string => {
  if (!employee || typeof employee !== 'object') {
    return ''
  }

  const value = (employee as Record<string, unknown>)[field]
  return value === undefined || value === null ? '' : String(value)
}

const getLatestFinishedReview = (reviews: Review[]): Review | undefined => {
  return [...reviews]
    .filter(review => review.finishDate)
    .sort((reviewA, reviewB) => {
      const dateA = new Date(reviewA.finishDate || reviewA.createdDate || 0).getTime()
      const dateB = new Date(reviewB.finishDate || reviewB.createdDate || 0).getTime()
      return dateB - dateA
    })[0]
}

const getNumericAnswerValue = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim()) {
    const parsedValue = Number(value)
    return Number.isFinite(parsedValue) ? parsedValue : undefined
  }
  return undefined
}

const getTopicAverage = (review: Review | undefined, aliases: string[]): string => {
  if (!review) {
    return ''
  }

  const topicQuestions = review.answers
    .filter(answer => {
      const topicName = answer.topic?.name?.trim().toLowerCase()
      return topicName ? aliases.some(alias => alias.toLowerCase() === topicName) : false
    })
    .filter(answer => answer.type === QuestionTypeEnum.NUMERIC || answer.type === QuestionTypeEnum.SCALE)
    .sort((answerA, answerB) => (answerA.order ?? 0) - (answerB.order ?? 0))

  const selectedAnswer = topicQuestions[topicQuestions.length - 1]
  if (!selectedAnswer) {
    return ''
  }

  const value = getNumericAnswerValue(selectedAnswer.answer) ?? getNumericAnswerValue(selectedAnswer.suggestedAnswer)
  return value !== undefined ? String(Math.round(value)) : ''
}

const buildExportRow = async (developer: TalentRankedDeveloper): Promise<ExportRow> => {
  const selectedCategory = developer.selectedTalentRank || ''
  const suggestedCategory = developer.suggestedTalentRank || ''
  const manager =
    developer.leaders
      ?.map(leader => leader.name)
      .filter(Boolean)
      .join(', ') || ''

  try {
    const [employee, reviews] = await Promise.all([
      EmployeeService.findOne(developer.id),
      ReviewService.findByEmployeeId(developer.id),
    ])

    const latestFinishedReview = getLatestFinishedReview(reviews)
    const metadata = parseMetadata(employee.metadata)

    return {
      id: String(employee.id ?? developer.id),
      name: employee.name || developer.name || '',
      email: employee.email || developer.email || '',
      job_title: employee.jobTitle || developer.jobTitle || '',
      role: employee.role || '',
      salary_grade: employee.salaryGrade || '',
      ms_id: getEmployeeFieldString(employee, 'msId') || getMetadataString(metadata, ['msId', 'ms_id', 'MS ID']),
      manager,
      category: selectedCategory || suggestedCategory,
      'Employee ID':
        getEmployeeFieldString(employee, 'employeeId') ||
        getMetadataString(metadata, ['employeeId', 'employee_id', 'Employee ID']),
      'Job Function': getMetadataString(metadata, ['jobFunction', 'job_function', 'Job Function']),
      communication_avg: getTopicAverage(latestFinishedReview, CATEGORY_TOPIC_ALIASES.communication_avg),
      professional_skills_avg: getTopicAverage(latestFinishedReview, CATEGORY_TOPIC_ALIASES.professional_skills_avg),
      commitment_avg: getTopicAverage(latestFinishedReview, CATEGORY_TOPIC_ALIASES.commitment_avg),
      growth_mindset_avg: getTopicAverage(latestFinishedReview, CATEGORY_TOPIC_ALIASES.growth_mindset_avg),
      productivity_avg: getTopicAverage(latestFinishedReview, CATEGORY_TOPIC_ALIASES.productivity_avg),
      ai_avg: getTopicAverage(latestFinishedReview, CATEGORY_TOPIC_ALIASES.ai_avg),
      selected_category: selectedCategory,
      suggested_category: suggestedCategory,
      override_reason: developer.overrideReason || '',
    }
  } catch {
    return {
      id: String(developer.id),
      name: developer.name || '',
      email: developer.email || '',
      job_title: developer.jobTitle || '',
      role: '',
      salary_grade: '',
      ms_id: '',
      manager,
      category: selectedCategory || suggestedCategory,
      'Employee ID': '',
      'Job Function': '',
      communication_avg: '',
      professional_skills_avg: '',
      commitment_avg: '',
      growth_mindset_avg: '',
      productivity_avg: '',
      ai_avg: '',
      selected_category: selectedCategory,
      suggested_category: suggestedCategory,
      override_reason: developer.overrideReason || '',
    }
  }
}

const TopTalentPage = (): JSX.Element => {
  const [enabledFilter, setEnabledFilter] = useState<string | undefined>(undefined)
  const [isExporting, setIsExporting] = useState(false)

  const {
    data: developers,
    isLoading,
    isError,
  } = useQuery<TalentRankedDeveloper[]>(['top-talent-developers', enabledFilter], () => {
    const tenX = enabledFilter === '10x' ? true : undefined
    const nineX = enabledFilter === '9x' ? true : undefined
    return EmployeeService.getAllTopTalentDevelopers(tenX, nineX)
  })

  const { data: allDevelopers } = useQuery<TalentRankedDeveloper[]>(['top-talent-developers-all'], () =>
    EmployeeService.getAllTopTalentDevelopers()
  )

  const handleDeveloperClick = (developer: TalentRankedDeveloper): void => {
    history.push(createPathToEmployeeResults(developer.id))
  }

  const handleExportToCSV = async () => {
    try {
      setIsExporting(true)
      const exportRows = await Promise.all((developers ?? []).map(buildExportRow))
      const csvContent = buildTopTalentCsv(exportRows)
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `top-talent-export-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting to CSV:', error)
    } finally {
      setIsExporting(false)
    }
  }

  const handleHighlightFilterSelected = (highlight: string) => () => {
    setEnabledFilter(enabledFilter === highlight ? undefined : highlight)
  }

  const highlightsCount: Record<string, number> = {
    '10x': allDevelopers?.filter(d => d.selectedTalentRank?.toLowerCase() === '10x').length ?? 0,
    '9x': allDevelopers?.filter(d => d.selectedTalentRank?.toLowerCase() === '9x').length ?? 0,
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )
    }

    if (isError) {
      return (
        <ErrorMessage
          body={<p>We could not retrieve the talent information right now. Please try again in a few minutes.</p>}
          className="mt-10"
        />
      )
    }

    return <TopTalentTable developers={developers ?? []} onDeveloperClick={handleDeveloperClick} />
  }

  return (
    <main className="container mx-auto flex-1 pb-20">
      <section className="flex flex-col gap-6">
        <header className="flex justify-between items-center gap-2">
          <h1 className="text-5xl font-semibold text-dark-blue">Top Talent Highlights</h1>
          <AppButton
            type="info"
            onClick={handleExportToCSV}
            className="flex items-center gap-2"
            disabled={isExporting || isLoading}
          >
            <DownloadIcon color="white" />
            {isExporting ? 'Exporting...' : 'Export to CSV'}
          </AppButton>
        </header>
        <div className="flex flex-wrap gap-3">
          {AVAILABLE_EMPLOYEE_HIGHLIGHTS.map(highlight => {
            const highlightValue = highlightsCount[highlight.value.toLowerCase()]
            return (
              <AppFilterButton
                key={highlight.value}
                active={enabledFilter === highlight.value.toLowerCase()}
                onClick={handleHighlightFilterSelected(highlight.value.toLowerCase())}
              >
                {EMPLOYEE_HIGHLIGHT_ICON_MAP[highlight.value]}&nbsp;&nbsp;{highlight.label}
                {!!highlightValue ? ` (${highlightValue})` : null}
              </AppFilterButton>
            )
          })}
        </div>
        {renderContent()}
      </section>
    </main>
  )
}

export default TopTalentPage
